<template>
  <article class="library-article">
    <!-- 非职业专题的类别 -->
    <template v-if="!isCareer">
      <!-- <breadcrumb :data="breadcrumbList"></breadcrumb> -->
      <page-filter
        :data="[
          {
            name: '分类',
            value: categoryValue[0],
            subValue: categoryValue[1],
            list: categoryList,
            subList: subCategoryList,
          },
          {
            name: '排序',
            value: sort,
            list: sortList,
          },
        ]"
        @first-click="reportFilterClick"
        @second-click="reportFilterClick"
        class="library-article-filter"
      ></page-filter>
    </template>

    <!-- 职业专题展示banner图 -->
    <img :src="bannerData.banner" class="library-article-banner" v-if="isCareer && bannerData"/>

    <template v-if="postData">
      <div class="library-article-list">
        <article-unit
          class="library-article-list-item"
          v-for="item in postData.list"
          :data="item"
          :key="item.id"
          @click.native="reportClick({ click: 'content', type: item.id })"
        ></article-unit>
      </div>

      <pagination
        :total="postData.count"
        :size="pageSize"
        :page="page"
        @change="handlePageChange"
      ></pagination>
    </template>
  </article>
</template>

<script>
import PageFilter from '@/components/page-filter';
import ArticleUnit from '@/components/article-unit';
import { query2Search, locateToQueryUrl } from '@/common/utils';
import mixinAsyncData from '@/common/mixin/mixin-async-data';

const reportKeyMap = {
  职业专题: 'job',
  高效技巧: 'skill',
  了解WPS: 'wps',
};

const reportSortMap = {
  hot: 'number',
  fav: 'collect',
  newest: 'time',
};

async function asyncData({ params, query, app }) {
  const { sort = 'hot' } = query;
  const page = query.page ? +query.page : 1;
  const cgId = params.cgId ? +params.cgId : '';
  const postType = 2;
  const pageSize = 10;

  const [categoryMenuRes, postRes, bannerRes] = await Promise.all([
    app.$service.learning.getCategoriesMenu(postType),
    app.$service.learning.getPostList({
      postType,
      category: cgId,
      sort,
      page,
      pageSize,
    }),
    app.$service.learning.getCategoryBanner(cgId).catch(() => null),
  ]);

  const categoryMenu = categoryMenuRes?.data || [];
  const postData = postRes && postRes.data;
  const bannerData = bannerRes && bannerRes.data;

  const isCurrentMenu = (item) => {
    if (item.id === cgId) return true;

    if (item.children && item.children.length) {
      return !!item.children.find((cItem) => isCurrentMenu(cItem));
    }
    return false;
  };
  const currentMenu = categoryMenu.find((item) => isCurrentMenu(item));

  // 是否职业专题
  const isCareer = currentMenu && currentMenu.name === '职业专题';

  // 计算map，供页面使用
  const categoryMap = {};
  currentMenu &&
    currentMenu.children.forEach((item) => {
      categoryMap[item.id] = item;

      item.children.forEach((subItem) => {
        categoryMap[subItem.id] = subItem;
      });
    });

  return {
    cgId,
    page,
    pageSize,
    sort,
    currentMenu,
    postData,
    categoryMap,
    isCareer,
    bannerData,
  };
}

export default {
  head() {
    let header = this.$head.libraryHead();

    const { currentMenu } = this;
    if (currentMenu && currentMenu.name) {
      header = this.$head.libraryListHead(currentMenu.name);
    }

    return header;
  },

  data() {
    return {
      cgId: '',
      page: 1,
      pageSize: 10,
      sort: 'hot',
      currentMenu: null,
      postData: { list: [] },
      categoryMap: null,
      bannerData: null,
      isCareer: false,
    };
  },

  components: {
    PageFilter,
    ArticleUnit,
  },

  mixins: [mixinAsyncData(asyncData)],

  computed: {
    breadcrumbList({ categoryValue, categoryMap, currentMenu }) {
      const list = [
        {
          name: '首页',
          url: '/learning',
        },
        {
          name: 'Office图书馆',
          url: '/learning/article/index.html',
        },
      ];

      if (currentMenu) {
        list.push({
          name: currentMenu.name,
          url: `/learning/article/index/cg/${currentMenu.id}`,
        });
      }

      categoryValue.forEach((item) => {
        const cItem = categoryMap[item];
        list.push({
          name: cItem.name,
          url: `/learning/article/index/cg/${cItem.id}`,
        });
      });

      return list;
    },

    // 选中项目的值
    categoryValue({ cgId, categoryMap }) {
      let res = [];

      if (!cgId || !categoryMap) return res;

      // 当前选中的项
      const currentCategory = categoryMap[cgId];

      if (!currentCategory) return res;

      // 假如父元素存在，则记录父元素及自身的值
      if (categoryMap[currentCategory.parent_id]) {
        res = [currentCategory.parent_id, currentCategory.id];
      } else {
        // 父元素不存在，只记录自身的值
        res = [currentCategory.id];
      }

      return res;
    },

    categoryList({ currentMenu }) {
      const res = [];

      if (currentMenu) {
        res.push({
          name: '全部',
          value: undefined,
          url: `/learning/article/index/cg/${currentMenu.id}`,
        });

        currentMenu.children.forEach((item) => {
          res.push({
            name: item.name,
            value: item.id,
            url: `/learning/article/index/cg/${item.id}`,
          });
        });
      }

      return res;
    },

    subCategoryList({ categoryValue, categoryMap }) {
      const res = [];
      if (!categoryValue.length || !categoryMap) return res;

      const parentCategory = categoryMap[categoryValue[0]];
      if (parentCategory && parentCategory.children.length) {
        res.push({
          name: '全部',
          value: undefined,
          url: `/learning/article/index/cg/${parentCategory.id}`,
        });

        parentCategory.children.forEach((item) => {
          res.push({
            name: item.name,
            value: item.id,
            url: `/learning/article/index/cg/${item.id}`,
          });
        });
      }

      return res;
    },

    sortList() {
      const { cgId } = this;
      const { query = {} } = this.$route;

      const sortList = [
        {
          name: '学习人数',
          value: 'hot',
        },
        {
          name: '发布时间',
          value: 'newest',
        },
        {
          name: '收藏人数',
          value: 'fav',
        },
      ];

      return sortList.map((item) => {
        let url = `/learning/article/index/cg/${cgId}`;
        return {
          ...item,
          url: `${url}${query2Search({
            ...query,
            page: 1,
            sort: item.value,
          })}`,
        };
      });
    },
  },

  methods: {
    handlePageChange(page) {
      locateToQueryUrl({ page });
    },

    /**
     * 上报筛选项点击
     */
    reportFilterClick(item, subItem) {
      if (item.name === '分类') {
        this.reportClick({ click: 'firstclass', type: subItem.id });
      } else if (item.name === '排序') {
        this.reportClick({ click: reportSortMap[subItem.value] });
      }
    },

    /**
     * 上报点击
     */
    reportClick({ click, type }) {
      this.$collect.listSkillarticleClick({
        click,
        type,
      });
    },

    afterMounted() {
      if (!this.postData || (this.postData.list && Array.isArray(this.postData.list) && this.postData.list.length == 0)) { // 如果list为空则表示筛选项错误，统一跳回首页
        this.$router.push('/learning')
      }
      const { currentMenu } = this;
      this.$collect.listSkillarticleShow({
        show: reportKeyMap[currentMenu && currentMenu.name],
      });
    },
  }
};
</script>

<style lang="scss" scoped>
.library-article {
  &-filter {
    padding: 2px 0 32px 0;
    border-bottom: 1px solid rgba(17, 19, 23, 0.1);
    margin-bottom: 32px;
  }

  &-list {
    margin-bottom: 32px;
    .article-unit {
      width: 603px;
    }
  }

  &-list-item {
    margin-bottom: 24px;
    margin-right: 24px;

    &:nth-of-type(2n) {
      margin-right: 0;
    }
  }

  &-banner {
    display: block;
    margin-bottom: 40px;
    width: 100%;
  }
}
</style>
